import Phaser from 'phaser';
import { GAME_HEIGHT, FONT_STACK } from '@/game/config';
import { getMusicMuted, setMusicMuted } from '@/game/storage';

/**
 * Bottom-left background-sound mute toggle — an understated speaker glyph (🔊 / 🔇). Shared by the
 * menu and the gameplay scenes so the control sits in the same corner everywhere. Language-neutral,
 * so it needs no i18n string. The mute state is persisted (see storage); `onToggle` lets a scene
 * react immediately — e.g. start/stop the live ambience bed during gameplay.
 */
export function addMuteToggle(
  scene: Phaser.Scene,
  opts: { depth?: number; onToggle?: (muted: boolean) => void } = {},
): Phaser.GameObjects.Text {
  const glyph = (): string => (getMusicMuted() ? '🔇' : '🔊');
  const t = scene.add.text(18, GAME_HEIGHT - 14, glyph(), {
    fontFamily: FONT_STACK, fontSize: '28px',
  }).setOrigin(0, 1).setAlpha(0.7);
  // Enlarge the tap target beyond the small glyph so it's easy to hit in the corner. The hit area
  // is in the text's local space (top-left corner = 0,0, unaffected by the 0,1 origin), so pad on
  // all sides. Keep it fixed even when the glyph swaps (🔊/🔇) — the padding covers the width diff.
  const HIT_PAD = 18;
  t.setInteractive(
    new Phaser.Geom.Rectangle(-HIT_PAD, -HIT_PAD, t.width + HIT_PAD * 2, t.height + HIT_PAD * 2),
    Phaser.Geom.Rectangle.Contains,
  );
  t.input!.cursor = 'pointer';
  if (opts.depth !== undefined) t.setDepth(opts.depth);
  t.on('pointerover', () => t.setAlpha(1));
  t.on('pointerout', () => t.setAlpha(0.7));
  // This runs before the scene-level pointerdown, so a scene that plays a "pop" on tap can suppress
  // it from inside onToggle (mirrors BoardScene's help-button handling).
  t.on('pointerdown', () => {
    const muted = !getMusicMuted();
    setMusicMuted(muted);
    t.setText(glyph());
    opts.onToggle?.(muted);
  });
  return t;
}
